import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../components/buttons/button";
import { EmptyState } from "../../components/empty";
import { Icon } from "../../components/icon/icon";
import { SearchInput } from "../../components/inputs/searchInput";
import { DiscountModal } from "../../components/modal/app_modals/discountModal";
import Pagination from "../../components/pagination";
import { Pill } from "../../components/pill";
import { TabNav } from "../../components/tabNav";
import { MiniHeader } from "../../components/typography/miniHeader";
import { PageHeader } from "../../components/typography/pageHeader";
import { BgWrapper } from "../../components/wrapper/bgWrapper";
import { useModalControl } from "../../customeHooks/useModalControl";
import { useGetDiscountsQuery } from "../../redux/features/event/event.api";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../utils/constants";
import { formatToNaira } from "../../utils/helpers";
import { QueryParams } from "../../utils/types";

export const DiscountsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [queryParams, setQueryParams] = useState<
    QueryParams & {
      status: string;
    }
  >({
    page: 1,
    limit: 20,
    search: undefined,
    sortOrder: "asc",
    status: "all",
  });

  const { modal, handleModalClose, handleModalOpen, modalOpen } =
    useModalControl();

  const {
    data: discounts,
    isFetching: fetchingDiscounts,
    refetch: refetchDiscounts,
  } = useGetDiscountsQuery(
    {
      ...queryParams,
      search: queryParams.search !== "" ? queryParams.search : undefined,
      status: queryParams.status === "all" ? undefined : queryParams.status,
    },
    { refetchOnMountOrArgChange: true },
  );

  const navigate = useNavigate();

  const tabs = [
    {
      title: "All",
      to: "all",
      onClick: (selected: string) => {
        setSearchParams({
          ...Object.fromEntries(searchParams.entries()),
          status: selected,
        });
      },
    },
    {
      title: "Active",
      to: "active",
      onClick: (selected: string) => {
        setSearchParams({
          ...Object.fromEntries(searchParams.entries()),
          status: selected,
        });
      },
    },
    {
      title: "Inactive",
      to: "inactive",
      onClick: (selected: string) => {
        setSearchParams({
          ...Object.fromEntries(searchParams.entries()),
          status: selected,
        });
      },
    },
  ];

  useEffect(() => {
    const q: QueryParams & {
      sortOrder: "asc" | "desc";
      status: string;
    } = JSON.parse(JSON.stringify(queryParams));

    q.status = searchParams.get("status") ?? "all";
    q.page = 1;

    setQueryParams(q);
    // eslint-disable-next-line
  }, [searchParams]);

  return (
    <div className="w-full">
      <div className="flex w-full items-center justify-between gap-4">
        <PageHeader header={"Discounts"} />

        <Button
          text="Add Discount"
          icon="plus"
          type="button"
          className="hidden max-w-fit md:flex"
          onClick={() => {
            handleModalOpen("addDiscountModal");
          }}
        />
      </div>

      <div className="mt-8 flex w-full flex-col gap-[2px]">
        <BgWrapper className="flex w-full flex-col gap-3">
          <TabNav
            navs={tabs}
            className="!min-w-full md:!min-w-fit md:!max-w-fit"
            tabClassName=""
            activeTab={queryParams.status!}
            variant="switcher"
            theme="plain"
          />

          <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
            <SearchInput
              searchFunction={(value) => {
                setQueryParams({
                  ...queryParams,
                  search: value,
                  page: 1,
                });
              }}
              placeholder="Search"
              className="md:!max-w-[300px]"
            />

            <div className="grid w-full grid-cols-2 items-center gap-3 md:flex md:w-fit md:flex-row">
              <Button
                type="button"
                onClick={() => {
                  if (queryParams.sortOrder === "asc") {
                    setQueryParams({ ...queryParams, sortOrder: "desc" });
                  } else {
                    setQueryParams({ ...queryParams, sortOrder: "asc" });
                  }
                }}
                text="Sort"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon={
                  queryParams.sortOrder === "asc" ? "order-asc" : "order-desc"
                }
                variation="grey"
              />

              <Button
                type="button"
                onClick={() => {}}
                text="Export"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon="export"
                variation="grey"
              />
            </div>
          </div>
        </BgWrapper>

        {fetchingDiscounts
          ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => {
              return (
                <BgWrapper key={num}>
                  <Skeleton
                    width={"100%"}
                    height={44}
                    className="rounded-2xl"
                    baseColor={SKELETON_BASE_COLOR}
                    highlightColor={SKELETON_HIGHLIGHT_COLOR}
                  />
                </BgWrapper>
              );
            })
          : undefined}

        {!fetchingDiscounts &&
        ![0, undefined, null].includes(discounts?.data?.data?.length) ? (
          <>
            {discounts?.data?.data!.map((discount) => {
              return (
                <BgWrapper
                  className="soft-shrink flex cursor-pointer items-center gap-5"
                  key={discount._id}
                  onClick={() => {
                    navigate(`/discounts/${discount.name}`);
                  }}
                >
                  <Icon
                    svg="ticket-percent"
                    width={24}
                    height={24}
                    className="text-blue"
                  />

                  <div className="flex-1">
                    <MiniHeader
                      header={`${discount.name.toUpperCase()}`}
                      subheader={`${discount.quantityType === "limited" ? `${discount.usageLimit}/${discount.quantity} Used` : "Unlimited"} ∙ ${discount.type === "percentage" ? `${discount.value}%` : `${formatToNaira(discount.value)}`} Discount`}
                      className="uppercase text-grey-900"
                    />
                  </div>

                  <Pill
                    text={discount.isActive ? "ACTIVE" : "INACTIVE"}
                    variation={discount.isActive ? "green" : "grey"}
                  />

                  <Icon
                    svg="caret-down"
                    width={20}
                    height={20}
                    className="-rotate-90 text-grey-600"
                  />
                </BgWrapper>
              );
            })}

            <BgWrapper>
              <Pagination
                currentPage={queryParams.page!}
                totalCount={discounts?.data.totalCount ?? 0}
                itemsPerPage={queryParams.limit!}
                onPageChange={(page) => {
                  setQueryParams({
                    ...queryParams,
                    page: page,
                  });
                }}
                showPagNumbers={false}
                containerClassName="!px-0 !py-0"
              />
            </BgWrapper>
          </>
        ) : undefined}

        {!fetchingDiscounts &&
        [0, undefined, null].includes(discounts?.data?.data?.length) ? (
          <EmptyState
            text="No Discount"
            subtext="Reduce ticket prices with discounts"
            textClassName="text-[24px]"
            subtextClassName="-mt-2"
          />
        ) : undefined}
      </div>

      <DiscountModal
        open={modalOpen && modal === "addDiscountModal"}
        handleClose={() => {
          handleModalClose();
        }}
        onSuccess={(discount) => {
          refetchDiscounts();
          handleModalClose();
          navigate(`/discounts/${discount?.name}`);
        }}
        mode="create"
      />
    </div>
  );
};
